import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import { get } from 'lodash';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { getUsers, FieldName } from 'admin/users/Users';
import { isAuthorized } from 'auth/Auth';
import { InputSearchDropdown, OPTIONAL_DATA_KEY } from 'common/input-search-dropdown/InputSearchDropdown';
import { OutsideClick } from 'common/outside-click/OutsideClick';
import { Overlay } from 'core/overlay/Overlay';
import { ButtonVariant } from 'core/util/Bootstrap';
import { SortOrder, Operator } from 'core/util/Search';
import { ReviewAttribution } from 'reviews2/feed/review-yeti/attribution/ReviewAttribution';
import { useReview, ReviewActionType } from 'reviews2/feed/review-yeti/Review.store';
import { ContextProps, ReadOnlyReason } from 'reviews2/feed/review-yeti/Review.types';
import { AttributionType } from 'reviews2/feed/Reviews.types';
import { addAttribution } from 'reviews2/Reviews.api';

import 'reviews2/feed/review-yeti/attributions/ReviewAttributions.scss';

const getAttributionsByType = (selectedType: AttributionType, attributions?: any[]): any[] | undefined => {
    return attributions?.filter(({ type }) => type === selectedType);
};

enum Mode {
    View = 1,
    Add,
}

const OVERLAY_AREA = 'r4e-review-attributions';

export const ReviewAttributions: FunctionComponent<ContextProps> = ({ context }) => {
    const { t } = useTranslation('reviews2');
    const [{ review }, dispatch] = useReview();
    const { options, actions } = context;
    const [currentMode, setCurrentMode] = useState<Mode>(Mode.View);
    const [showHeader, setShowHeader] = useState<boolean>(false);
    const [showActions, setShowActions] = useState<boolean>(false);
    const displayAttributions = useMemo(() => {
        if (review?.user_attribution?.length) {
            const { user_attribution: attributions } = review!;
            const requestor = getAttributionsByType(AttributionType.Requestor, attributions);
            const mention = getAttributionsByType(AttributionType.Mention, attributions);
            const manual = getAttributionsByType(AttributionType.Manual, attributions);
            const sorted = [];

            if (requestor?.length) {
                sorted.push(...requestor);
            }

            if (mention?.length) {
                sorted.push(...mention);
            }

            if (manual?.length) {
                sorted.push(...manual);
            }

            return sorted;
        }

        return [];
    }, [review]);
    const existingUserIds = useMemo(
        () => displayAttributions?.filter((attr) => attr.visible)?.map((attr) => String(attr.user_id)),
        [displayAttributions],
    );

    useEffect(() => {
        if (
            currentMode !== Mode.Add &&
            isAuthorized({ actions: [ActionId.ReviewAttributions.Add] }) &&
            ![ReadOnlyReason.View, ReadOnlyReason.Reports].includes(options?.readOnlyReason as ReadOnlyReason)
        ) {
            setShowActions(true);
        } else if (showActions) {
            setShowActions(false);
        }

        if (
            displayAttributions.length ||
            (isAuthorized({ actions: [ActionId.ReviewAttributions.Add] }) &&
                ![ReadOnlyReason.View, ReadOnlyReason.Reports].includes(options?.readOnlyReason as ReadOnlyReason))
        ) {
            setShowHeader(true);
        } else if (showHeader) {
            setShowHeader(false);
        }
    }, [setShowActions, setShowHeader, currentMode, options, showActions, showHeader, displayAttributions]);

    const searchUsers = useCallback(
        async (search: string) => {
            const filters = [
                {
                    name: FieldName.Search,
                    values: [search],
                    operator: Operator.iLike,
                },
                {
                    name: FieldName.UserId,
                    values: existingUserIds!,
                    operator: Operator.NotEqual,
                },
                {
                    name: FieldName.Active,
                    values: ['true'],
                },
            ];
            const sort = {
                name: FieldName.DisplayName,
                sortOrder: SortOrder.Asc,
            };
            const users = await getUsers(review!.r4e_agency_id, review!.r4e_tenant_id, {
                start: 0,
                count: 10,
                sorts: [sort],
                filters,
            });

            return users.map((user) => ({ displayName: user.displayName, [OPTIONAL_DATA_KEY]: user.id }));
        },
        [review, existingUserIds],
    );

    const selectUser = useCallback(
        async (selected) => {
            const { [OPTIONAL_DATA_KEY]: userId } = selected;
            const args = {
                userId,
                attributeType: AttributionType.Manual,
            };

            // eslint-disable-next-line camelcase
            const { user_attribution = [] } = await trackPromise(
                addAttribution(review!.r4e_tenant_id, review!.document_id, args),
                `${OVERLAY_AREA}-${review!.document_id}`,
            );

            dispatch({
                type: ReviewActionType.UpdateReview,
                review: { user_attribution },
            });
            setCurrentMode(Mode.View);
        },
        [dispatch, review],
    );

    if (
        !review ||
        (!isAuthorized({ actions: [ActionId.ReviewAttributions.View] }) &&
            !get(actions, ActionId.ReviewAttributions.View))
    ) {
        return null;
    }

    return (
        <div className="r4e-review-yeti-attributions">
            <Overlay area={`${OVERLAY_AREA}-${review!.document_id}`} />
            <div>
                {showActions ? (
                    <Button variant={ButtonVariant.OutlinePrimary} onClick={() => setCurrentMode(Mode.Add)}>
                        {t('REVIEW_ATTRIBUTIONS')}
                        <i className="icon-plus" />
                    </Button>
                ) : null}
                {currentMode === Mode.Add && (
                    <span className="add">
                        <OutsideClick outsideClickHandler={() => setCurrentMode(Mode.View)}>
                            <InputSearchDropdown
                                value=""
                                placeholder={t('REVIEW_ADD_ATTRIBUTION')}
                                onDropdownOptionSelect={(option) => selectUser(option)}
                                getDropdownData={(search: string) => searchUsers(search)}
                            />
                        </OutsideClick>
                    </span>
                )}
            </div>
            <div className="attributions">
                {displayAttributions?.map((attribution) => (
                    <ReviewAttribution key={attribution.user_id} context={context} attribution={attribution} />
                ))}
            </div>
        </div>
    );
};
